import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

type Status = 'idle' | 'sending' | 'sent' | 'error';

export default function SignIn() {
  const { session, loading, sendMagicLink } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  // Already signed in (or just came back from a link) -- go where they meant.
  if (!loading && session) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || status === 'sending') return;

    setStatus('sending');
    setError('');
    try {
      await sendMagicLink(email.trim());
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send the link.');
    }
  }

  if (status === 'sent') {
    return (
      <div className="auth-card">
        <h1>Check your email</h1>
        <p className="auth-hint">
          If <strong>{email.trim()}</strong> has an account, a sign-in link is on
          its way. Open it on this device — the link signs you in and expires
          shortly.
        </p>
        <button
          type="button"
          className="btn btn-outline mt-2"
          onClick={() => setStatus('idle')}
        >
          Use a different address
        </button>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <h1>Sign in</h1>
      <p className="auth-hint">
        Enter your email and we'll send you a sign-in link. No password needed.
      </p>

      <form onSubmit={handleSubmit}>
        <label className="auth-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="auth-input"
        />

        {status === 'error' && <p className="auth-error">{error}</p>}

        <button
          type="submit"
          className="btn btn-primary mt-2"
          disabled={!email.trim() || status === 'sending'}
        >
          {status === 'sending' ? 'Sending…' : 'Send sign-in link'}
        </button>
      </form>

      <p className="auth-footnote">
        WatchWatch is invite-only. Ask Mark if you need an account.
      </p>
    </div>
  );
}
